import { useAPIQuery } from "@dev2_techwalnut/tw-api-query-hook";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import secureLocalStorage from "react-secure-storage";

const tabs = [
    {
        key: "audit",
        label: "Audit Logs",
        icon: "ni ni-list-index",
        privilege: "bridge_console_audit_logs",
    },
    {
        key: "errors",
        label: "Error Logs",
        icon: "ni ni-alert-circle",
        privilege: "bridge_console_error_logs",
    },
    {
        key: "roles",
        label: "Roles",
        icon: "ni ni-users",
        privilege: "bridge_console_roles",
    },
    {
        key: "users",
        label: "Users & Readiness",
        icon: "ni ni-user-check",
        privilege: "bridge_console_roles",
    },
];

const normalizePrivileges = (value) => {
    if (Array.isArray(value)) {
        return value;
    }
    if (typeof value === "string") {
        return [value];
    }
    return [];
};

const getPayload = (response, fallback) =>
    response?.results?.data ?? response?.data?.results?.data ?? fallback;

const getCount = (response, fallback = 0) =>
    response?.count ?? response?.data?.count ?? fallback;

const getNumPages = (response) =>
    response?.num_pages ?? response?.data?.num_pages ?? 1;

const formatDate = (value) => {
    if (!value) {
        return "-";
    }
    return moment(value).format("DD MMM YYYY, hh:mm A");
};

const formatValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return "-";
    }
    return value;
};

const formatDetailValue = (value) => {
    if (value === null || value === undefined || value === "") {
        return "-";
    }
    if (typeof value === "string") {
        return value;
    }
    return JSON.stringify(value, null, 2);
};

const DetailRow = ({ label, value }) => (
    <div className="row g-2 py-1">
        <div className="col-sm-3">
            <span className="sub-text">{label}</span>
        </div>
        <div className="col-sm-9">
            <span>{formatValue(value)}</span>
        </div>
    </div>
);

const DetailBlock = ({ title, value }) => (
    <div className="mb-3">
        <span className="preview-title overline-title">{title}</span>
        <pre className="bg-lighter p-2 rounded small mb-0 overflow-auto">
            {formatDetailValue(value)}
        </pre>
    </div>
);

const Spinner = () => (
    <div className="spinner-border text-primary ms-2" role="status">
        <span className="sr-only"></span>
    </div>
);

const StatCard = ({ label, value, icon }) => (
    <div className="col-sm-6 col-xl-3">
        <div className="card card-bordered">
            <div className="card-inner">
                <div className="d-flex align-items-center">
                    <div className="user-avatar bg-primary-dim">
                        <em className={`icon ${icon}`}></em>
                    </div>
                    <div className="ms-3">
                        <span className="sub-text">{label}</span>
                        <h5 className="mb-0">{value}</h5>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

const EmptyState = ({ isLoading, text }) => {
    if (isLoading) {
        return null;
    }
    return (
        <tr>
            <td className="text-center text-soft py-4" colSpan={8}>
                {text}
            </td>
        </tr>
    );
};

const PaginationControls = ({ page, pageCount, onPageChange }) => (
    <div className="d-flex align-items-center justify-content-end gap-2 pt-3">
        <button
            type="button"
            className="btn btn-sm btn-outline-light"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
        >
            <em className="icon ni ni-chevron-left"></em>
        </button>
        <span className="sub-text">
            Page {page} of {pageCount || 1}
        </span>
        <button
            type="button"
            className="btn btn-sm btn-outline-light"
            disabled={page >= pageCount}
            onClick={() => onPageChange(page + 1)}
        >
            <em className="icon ni ni-chevron-right"></em>
        </button>
    </div>
);

const ReadinessBadge = ({ ready, readyLabel = "Ready", missingLabel = "Missing" }) =>
    ready ? (
        <span className="badge badge-dim bg-success">{readyLabel}</span>
    ) : (
        <span className="badge badge-dim bg-danger">{missingLabel}</span>
    );

const BridgeConsole = () => {
    const userPrivileges = useMemo(
        () => normalizePrivileges(secureLocalStorage.getItem("privileges")),
        []
    );
    const hasPrivilege = (privilege) => userPrivileges.includes(privilege);
    const visibleTabs = useMemo(
        () => tabs.filter((tab) => userPrivileges.includes(tab.privilege)),
        [userPrivileges]
    );
    const canViewAuditLogs = hasPrivilege("bridge_console_audit_logs");
    const canViewErrorLogs = hasPrivilege("bridge_console_error_logs");
    const canViewRoles = hasPrivilege("bridge_console_roles");

    const [activeTab, setActiveTab] = useState(visibleTabs?.[0]?.key ?? "");
    const [entries, setEntries] = useState(20);
    const [auditPage, setAuditPage] = useState(1);
    const [errorPage, setErrorPage] = useState(1);
    const [auditProcess, setAuditProcess] = useState("");
    const [auditActorUsername, setAuditActorUsername] = useState("");
    const [userPage, setUserPage] = useState(1);
    const [userSearch, setUserSearch] = useState("");
    const [userRoleFilter, setUserRoleFilter] = useState("");
    const [userReadinessFilter, setUserReadinessFilter] = useState("");
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedErrorLog, setSelectedErrorLog] = useState(null);

    useEffect(() => {
        if (
            visibleTabs.length > 0 &&
            !visibleTabs.some((tab) => tab.key === activeTab)
        ) {
            setActiveTab(visibleTabs[0].key);
        }
    }, [activeTab, visibleTabs]);

    const { useFetch: useFetchAuditLogs } = useAPIQuery({
        key: "bridge-console-audit-logs",
        endpoint: "/bridge-console/audit-logs/recent/",
    });
    const { useFetch: useFetchErrorLogs } = useAPIQuery({
        key: "bridge-console-error-logs",
        endpoint: "/bridge-console/error-logs/recent/",
    });
    const { useFetch: useFetchRoles } = useAPIQuery({
        key: "bridge-console-roles",
        endpoint: "/bridge-console/roles/",
    });
    const { useFetch: useFetchUserReadiness } = useAPIQuery({
        key: "bridge-console-user-readiness",
        endpoint: "/bridge-console/users/readiness/",
    });

    const auditLogsQuery = useFetchAuditLogs({
        params: {
            entries,
            page_no: auditPage,
            process: auditProcess || undefined,
            actor_username: auditActorUsername || undefined,
        },
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 2,
        retry: 2,
        keepPlaceholder: true,
        refetchOnReconnect: true,
        enabled: canViewAuditLogs,
    });
    const errorLogsQuery = useFetchErrorLogs({
        params: {
            entries,
            page_no: errorPage,
        },
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 2,
        retry: 2,
        keepPlaceholder: true,
        refetchOnReconnect: true,
        enabled: canViewErrorLogs,
    });
    const rolesQuery = useFetchRoles({
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 5,
        retry: 2,
        keepPlaceholder: true,
        refetchOnReconnect: true,
        enabled: canViewRoles && activeTab === "roles",
    });
    const userReadinessQuery = useFetchUserReadiness({
        params: {
            entries,
            page_no: userPage,
            q: userSearch || undefined,
            role_value: userRoleFilter || undefined,
            readiness: userReadinessFilter || undefined,
        },
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 2,
        retry: 2,
        keepPlaceholder: true,
        refetchOnReconnect: true,
        enabled: canViewRoles && activeTab === "users",
    });

    const auditLogs = useMemo(
        () => getPayload(auditLogsQuery.data, []),
        [auditLogsQuery.data]
    );
    const errorLogs = useMemo(
        () => getPayload(errorLogsQuery.data, []),
        [errorLogsQuery.data]
    );
    const rolePayload = useMemo(
        () => getPayload(rolesQuery.data, { roles: [], privileges: [] }),
        [rolesQuery.data]
    );
    const roles = rolePayload?.roles ?? [];
    const privileges = rolePayload?.privileges ?? [];
    const userReadinessPayload = useMemo(
        () =>
            getPayload(userReadinessQuery.data, {
                users: [],
                summary: {},
                available_roles: [],
                readiness_filters: [],
            }),
        [userReadinessQuery.data]
    );
    const readinessUsers = userReadinessPayload.users ?? [];
    const readinessSummary = userReadinessPayload.summary ?? {};
    const availableRoles = userReadinessPayload.available_roles ?? [];
    const readinessFilters =
        userReadinessPayload.readiness_filters ?? [];
    const totalUsers = roles.reduce(
        (sum, role) => sum + (role?.user?.length ?? 0),
        0
    );

    const auditPageCount = getNumPages(auditLogsQuery.data);
    const errorPageCount = getNumPages(errorLogsQuery.data);
    const userPageCount = getNumPages(userReadinessQuery.data);

    const handleEntriesChange = (event) => {
        setEntries(Number(event.target.value));
        setAuditPage(1);
        setErrorPage(1);
        setUserPage(1);
    };

    return (
        <>
            <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between">
                    <div className="nk-block-head-content d-flex align-items-center">
                        <h3 className="nk-block-title page-title">
                            Bridge Console
                        </h3>
                        {((canViewAuditLogs && auditLogsQuery.isFetching) ||
                            (canViewErrorLogs && errorLogsQuery.isFetching) ||
                            (canViewRoles &&
                                activeTab === "roles" &&
                                rolesQuery.isFetching) ||
                            (canViewRoles &&
                                activeTab === "users" &&
                                userReadinessQuery.isFetching)) && (
                            <Spinner />
                        )}
                    </div>
                    <div className="nk-block-head-content">
                        <div className="form-control-wrap">
                            <select
                                className="form-select"
                                value={entries}
                                onChange={handleEntriesChange}
                            >
                                <option value={10}>10 entries</option>
                                <option value={20}>20 entries</option>
                                <option value={50}>50 entries</option>
                                <option value={100}>100 entries</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <div className="nk-block">
                <div className="row g-gs">
                    {canViewAuditLogs && (
                        <StatCard
                            label="Audit Events"
                            value={getCount(auditLogsQuery.data)}
                            icon="ni-list-index"
                        />
                    )}
                    {canViewErrorLogs && (
                        <StatCard
                            label="Error Events"
                            value={getCount(errorLogsQuery.data)}
                            icon="ni-alert-circle"
                        />
                    )}
                    {canViewRoles && (
                        <StatCard
                            label="Roles"
                            value={roles.length}
                            icon="ni-users"
                        />
                    )}
                    {canViewRoles && (
                        <StatCard
                            label="Users Mapped"
                            value={totalUsers}
                            icon="ni-user-list"
                        />
                    )}
                </div>
            </div>

            <div className="nk-block">
                <div className="card card-bordered">
                    <div className="card-inner border-bottom">
                        <ul className="nav nav-tabs mt-n3">
                            {visibleTabs.map((tab) => (
                                <li className="nav-item" key={tab.key}>
                                    <button
                                        type="button"
                                        className={`nav-link ${
                                            activeTab === tab.key
                                                ? "active"
                                                : ""
                                        }`}
                                        onClick={() => setActiveTab(tab.key)}
                                    >
                                        <em
                                            className={`icon ${tab.icon} me-1`}
                                        ></em>
                                        {tab.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {visibleTabs.length === 0 && (
                        <div className="card-inner">
                            <div className="alert alert-light mb-0">
                                No Bridge Console tabs are assigned to this
                                user.
                            </div>
                        </div>
                    )}

                    {activeTab === "audit" && (
                        <div className="card-inner">
                            <div className="row g-3 align-items-end mb-3">
                                <div className="col-md-4">
                                    <label className="form-label">
                                        Process
                                    </label>
                                    <input
                                        className="form-control"
                                        value={auditProcess}
                                        onChange={(event) => {
                                            setAuditProcess(
                                                event.target.value
                                            );
                                            setAuditPage(1);
                                        }}
                                        placeholder="sales_order, qc, dispatch"
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">
                                        Actor
                                    </label>
                                    <input
                                        className="form-control"
                                        value={auditActorUsername}
                                        onChange={(event) => {
                                            setAuditActorUsername(
                                                event.target.value
                                            );
                                            setAuditPage(1);
                                        }}
                                        placeholder="Search username"
                                    />
                                </div>
                            </div>
                            <div className="table-responsive">
                                <table className="table table-tranx">
                                    <thead>
                                        <tr className="tb-tnx-head">
                                            <th>Date</th>
                                            <th>Summary</th>
                                            <th>Process</th>
                                            <th>Action</th>
                                            <th>Actor</th>
                                            <th>Target</th>
                                            <th>Request</th>
                                            <th>Changed Fields</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {auditLogs.map((log) => (
                                            <tr key={log.id}>
                                                <td>
                                                    {formatDate(
                                                        log.occurred_at
                                                    )}
                                                </td>
                                                <td>{log.safe_summary}</td>
                                                <td>
                                                    <span className="badge badge-dim bg-info">
                                                        {formatValue(
                                                            log.process
                                                        )}
                                                    </span>
                                                </td>
                                                <td>
                                                    {formatValue(
                                                        log.action_code ||
                                                            log.action
                                                    )}
                                                </td>
                                                <td>
                                                    {formatValue(
                                                        log.actor?.full_name ||
                                                            log.actor_username ||
                                                            log.actor_id
                                                    )}
                                                    {log.actor?.username &&
                                                    log.actor?.username !==
                                                        log.actor?.full_name ? (
                                                        <div className="sub-text">
                                                            {
                                                                log.actor
                                                                    .username
                                                            }
                                                        </div>
                                                    ) : null}
                                                </td>
                                                <td>
                                                    {formatValue(
                                                        log.target
                                                            ?.model_name
                                                    )}
                                                    {log.target?.object_id ? (
                                                        <span className="text-soft">
                                                            {" "}
                                                            #{log.target.object_id}
                                                        </span>
                                                    ) : null}
                                                </td>
                                                <td>
                                                    {formatValue(
                                                        log.request?.method
                                                    )}{" "}
                                                    {formatValue(
                                                        log.request?.path
                                                    )}
                                                </td>
                                                <td>
                                                    {(log.changed_fields ?? [])
                                                        .slice(0, 4)
                                                        .join(", ") || "-"}
                                                    {(log.changed_fields ?? [])
                                                        .length > 4
                                                        ? ` +${
                                                              log
                                                                  .changed_fields
                                                                  .length - 4
                                                          }`
                                                        : ""}
                                                </td>
                                            </tr>
                                        ))}
                                        <EmptyState
                                            isLoading={auditLogsQuery.isFetching}
                                            text="No audit logs found."
                                        />
                                    </tbody>
                                </table>
                            </div>
                            <PaginationControls
                                page={auditPage}
                                pageCount={auditPageCount}
                                onPageChange={setAuditPage}
                            />
                        </div>
                    )}

                    {activeTab === "errors" && (
                        <div className="card-inner">
                            <div className="table-responsive">
                                <table className="table table-tranx">
                                    <thead>
                                        <tr className="tb-tnx-head">
                                            <th>Date</th>
                                            <th>Summary</th>
                                            <th>Status</th>
                                            <th>Method</th>
                                            <th>Path</th>
                                            <th>Source</th>
                                            <th>View Details</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {errorLogs.map((log) => (
                                            <tr key={log.id}>
                                                <td>
                                                    {formatDate(
                                                        log.occurred_at
                                                    )}
                                                </td>
                                                <td>{log.safe_summary}</td>
                                                <td>
                                                    <span className="badge badge-dim bg-danger">
                                                        {formatValue(
                                                            log.http_status_code
                                                        )}
                                                    </span>
                                                </td>
                                                <td>
                                                    {formatValue(
                                                        log.http_method
                                                    )}
                                                </td>
                                                <td>
                                                    {formatValue(
                                                        log.request_path
                                                    )}
                                                </td>
                                                <td>
                                                    {formatValue(
                                                        log.source?.file
                                                    )}
                                                    {log.source?.line ? (
                                                        <span className="text-soft">
                                                            :{log.source.line}
                                                        </span>
                                                    ) : null}
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-primary"
                                                        data-bs-toggle="modal"
                                                        data-bs-target="#bridge-error-detail-modal"
                                                        onClick={() =>
                                                            setSelectedErrorLog(
                                                                log
                                                            )
                                                        }
                                                    >
                                                        View Details
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        <EmptyState
                                            isLoading={errorLogsQuery.isFetching}
                                            text="No error logs found."
                                        />
                                    </tbody>
                                </table>
                            </div>
                            <PaginationControls
                                page={errorPage}
                                pageCount={errorPageCount}
                                onPageChange={setErrorPage}
                            />
                        </div>
                    )}

                    {activeTab === "roles" && (
                        <div className="card-inner">
                            <div className="row g-3 mb-4">
                                <div className="col-md-4">
                                    <div className="preview-block">
                                        <span className="preview-title overline-title">
                                            Privileges
                                        </span>
                                        <div className="h5">
                                            {privileges.length}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="table-responsive">
                                <table className="table table-tranx">
                                    <thead>
                                        <tr className="tb-tnx-head">
                                            <th>Role</th>
                                            <th>Value</th>
                                            <th>Level</th>
                                            <th>Users</th>
                                            <th>Privileges</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {roles.map((role) => (
                                            <tr key={role.id}>
                                                <td>
                                                    <strong>
                                                        {formatValue(
                                                            role.name
                                                        )}
                                                    </strong>
                                                </td>
                                                <td>
                                                    {formatValue(role.value)}
                                                </td>
                                                <td>
                                                    {formatValue(
                                                        role.hierarchy ??
                                                            role.level
                                                    )}
                                                </td>
                                                <td>
                                                    {(role.user ?? []).length}
                                                </td>
                                                <td>
                                                    {(role.privileges ?? [])
                                                        .slice(0, 6)
                                                        .map((privilege) => (
                                                            <span
                                                                className="badge badge-dim bg-light text-dark me-1 mb-1"
                                                                key={
                                                                    privilege.id
                                                                }
                                                            >
                                                                {
                                                                    privilege.value
                                                                }
                                                            </span>
                                                        ))}
                                                    {(role.privileges ?? [])
                                                        .length > 6 ? (
                                                        <span className="badge badge-dot bg-primary">
                                                            +
                                                            {(role.privileges ??
                                                                []).length - 6}
                                                        </span>
                                                    ) : null}
                                                </td>
                                            </tr>
                                        ))}
                                        <EmptyState
                                            isLoading={rolesQuery.isFetching}
                                            text="No roles found."
                                        />
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {activeTab === "users" && (
                        <div className="card-inner">
                            <div className="alert alert-light mb-3">
                                Review which users hold each role and whether
                                they can receive notifications via{" "}
                                <strong>email</strong> and{" "}
                                <strong>mobile push</strong> (FCM device token).
                            </div>
                            <div className="row g-gs mb-4">
                                <StatCard
                                    label="Active Users"
                                    value={readinessSummary.users_total ?? 0}
                                    icon="ni-users"
                                />
                                <StatCard
                                    label="Email Ready"
                                    value={readinessSummary.email_ready ?? 0}
                                    icon="ni-mail"
                                />
                                <StatCard
                                    label="Push Ready"
                                    value={readinessSummary.push_ready ?? 0}
                                    icon="ni-mobile"
                                />
                                <StatCard
                                    label="Fully Ready"
                                    value={readinessSummary.fully_ready ?? 0}
                                    icon="ni-check-circle"
                                />
                            </div>
                            <div className="row g-3 mb-3">
                                <div className="col-md-4">
                                    <input
                                        className="form-control"
                                        placeholder="Search user, email, name"
                                        value={userSearch}
                                        onChange={(event) => {
                                            setUserSearch(event.target.value);
                                            setUserPage(1);
                                        }}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <select
                                        className="form-select"
                                        value={userRoleFilter}
                                        onChange={(event) => {
                                            setUserRoleFilter(
                                                event.target.value
                                            );
                                            setUserPage(1);
                                        }}
                                    >
                                        <option value="">All roles</option>
                                        {availableRoles.map((role) => (
                                            <option
                                                key={role.id}
                                                value={role.role_value}
                                            >
                                                {role.role_name} (
                                                {role.role_value})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <select
                                        className="form-select"
                                        value={userReadinessFilter}
                                        onChange={(event) => {
                                            setUserReadinessFilter(
                                                event.target.value
                                            );
                                            setUserPage(1);
                                        }}
                                    >
                                        {readinessFilters.map((filter) => (
                                            <option
                                                key={
                                                    filter.value ||
                                                    "all-users"
                                                }
                                                value={filter.value}
                                            >
                                                {filter.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="row g-gs">
                                <div className="col-lg-8">
                                    <div className="table-responsive">
                                        <table className="table table-tranx">
                                            <thead>
                                                <tr className="tb-tnx-head">
                                                    <th>User</th>
                                                    <th>Roles</th>
                                                    <th>Email</th>
                                                    <th>Push</th>
                                                    <th>Devices</th>
                                                    <th>Details</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {readinessUsers.map((user) => (
                                                    <tr key={user.id}>
                                                        <td>
                                                            <strong>
                                                                {formatValue(
                                                                    user.name
                                                                )}
                                                            </strong>
                                                            <div className="sub-text">
                                                                {formatValue(
                                                                    user.username
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {(user.roles || [])
                                                                .length ? (
                                                                user.roles.map(
                                                                    (role) => (
                                                                        <span
                                                                            key={`${user.id}-${role.value}`}
                                                                            className="badge badge-dim bg-primary me-1 mb-1"
                                                                        >
                                                                            {
                                                                                role.value
                                                                            }
                                                                        </span>
                                                                    )
                                                                )
                                                            ) : (
                                                                <span className="badge badge-dim bg-light text-dark">
                                                                    No role
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <ReadinessBadge
                                                                ready={
                                                                    user
                                                                        .readiness
                                                                        ?.email_ready
                                                                }
                                                                readyLabel="Present"
                                                                missingLabel="Missing"
                                                            />
                                                            <div className="sub-text">
                                                                {formatValue(
                                                                    user.email
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <ReadinessBadge
                                                                ready={
                                                                    user
                                                                        .readiness
                                                                        ?.push_ready
                                                                }
                                                                readyLabel="Present"
                                                                missingLabel="Missing"
                                                            />
                                                            <div className="sub-text">
                                                                {
                                                                    user
                                                                        .readiness
                                                                        ?.devices_with_token_count
                                                                }{" "}
                                                                token(s)
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {user.readiness
                                                                ?.device_count ??
                                                                0}
                                                        </td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-primary"
                                                                onClick={() =>
                                                                    setSelectedUser(
                                                                        user
                                                                    )
                                                                }
                                                            >
                                                                View
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                                <EmptyState
                                                    isLoading={
                                                        userReadinessQuery.isFetching
                                                    }
                                                    text="No users found."
                                                />
                                            </tbody>
                                        </table>
                                    </div>
                                    <PaginationControls
                                        page={userPage}
                                        pageCount={userPageCount}
                                        onPageChange={setUserPage}
                                    />
                                </div>
                                <div className="col-lg-4">
                                    {selectedUser ? (
                                        <div className="card card-bordered">
                                            <div className="card-inner">
                                                <h6 className="title">
                                                    User Details
                                                </h6>
                                                <DetailRow
                                                    label="Name"
                                                    value={selectedUser.name}
                                                />
                                                <DetailRow
                                                    label="Username"
                                                    value={
                                                        selectedUser.username
                                                    }
                                                />
                                                <DetailRow
                                                    label="Email"
                                                    value={selectedUser.email}
                                                />
                                                <DetailRow
                                                    label="Mobile"
                                                    value={
                                                        selectedUser.mobile_number
                                                    }
                                                />
                                                <div className="mb-3">
                                                    <span className="preview-title overline-title">
                                                        Roles
                                                    </span>
                                                    {(selectedUser.roles || [])
                                                        .length ? (
                                                        selectedUser.roles.map(
                                                            (role) => (
                                                                <span
                                                                    key={`${selectedUser.id}-${role.value}-detail`}
                                                                    className="badge badge-dim bg-primary me-1 mb-1"
                                                                >
                                                                    {
                                                                        role.name
                                                                    }{" "}
                                                                    (
                                                                    {
                                                                        role.value
                                                                    }
                                                                    )
                                                                </span>
                                                            )
                                                        )
                                                    ) : (
                                                        <div className="sub-text">
                                                            No roles assigned
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="mb-2">
                                                    <ReadinessBadge
                                                        ready={
                                                            selectedUser
                                                                .readiness
                                                                ?.email_ready
                                                        }
                                                        readyLabel="Email ready"
                                                        missingLabel="Email missing"
                                                    />{" "}
                                                    <ReadinessBadge
                                                        ready={
                                                            selectedUser
                                                                .readiness
                                                                ?.push_ready
                                                        }
                                                        readyLabel="Push ready"
                                                        missingLabel="Push missing"
                                                    />
                                                </div>
                                                <div>
                                                    <span className="preview-title overline-title">
                                                        Registered Devices
                                                    </span>
                                                    {(selectedUser.devices ||
                                                        []).length ? (
                                                        selectedUser.devices.map(
                                                            (device) => (
                                                                <div
                                                                    key={
                                                                        device.id
                                                                    }
                                                                    className="border rounded p-2 mb-2"
                                                                >
                                                                    <div>
                                                                        <strong>
                                                                            {formatValue(
                                                                                device.mac_id
                                                                            )}
                                                                        </strong>
                                                                    </div>
                                                                    <div className="sub-text">
                                                                        {formatValue(
                                                                            device.device_type
                                                                        )}{" "}
                                                                        ·{" "}
                                                                        {device.has_token
                                                                            ? `Token ${device.token_hint}`
                                                                            : "No token"}
                                                                    </div>
                                                                </div>
                                                            )
                                                        )
                                                    ) : (
                                                        <div className="sub-text">
                                                            No devices registered
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="alert alert-light mb-0">
                                            Select a user to inspect roles,
                                            email, and device tokens.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div
                className="modal fade m-0 p-0"
                id="bridge-error-detail-modal"
                tabIndex="-1"
                aria-modal="true"
                role="dialog"
            >
                <div className="modal-dialog modal-xl" role="document">
                    <div className="modal-content">
                        <button
                            type="button"
                            className="close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                        >
                            <em className="icon ni ni-cross"></em>
                        </button>
                        <div className="modal-header">
                            <h5 className="modal-title">Error Details</h5>
                        </div>
                        <div className="modal-body">
                            {selectedErrorLog ? (
                                <>
                                    <DetailRow
                                        label="Time"
                                        value={formatDate(
                                            selectedErrorLog.occurred_at
                                        )}
                                    />
                                    <DetailRow
                                        label="Status"
                                        value={
                                            selectedErrorLog.http_status_code
                                        }
                                    />
                                    <DetailRow
                                        label="Type"
                                        value={
                                            selectedErrorLog.details?.error
                                                ?.type ||
                                            selectedErrorLog.error_type
                                        }
                                    />
                                    <DetailRow
                                        label="Message"
                                        value={
                                            selectedErrorLog.details?.error
                                                ?.message ||
                                            selectedErrorLog.error_message
                                        }
                                    />
                                    <DetailRow
                                        label="Method"
                                        value={
                                            selectedErrorLog.details?.request
                                                ?.method ||
                                            selectedErrorLog.http_method
                                        }
                                    />
                                    <DetailRow
                                        label="Path"
                                        value={
                                            selectedErrorLog.details?.request
                                                ?.path ||
                                            selectedErrorLog.request_path
                                        }
                                    />
                                    <DetailRow
                                        label="Source"
                                        value={`${formatValue(
                                            selectedErrorLog.details?.source
                                                ?.file ||
                                                selectedErrorLog.source?.file
                                        )}:${formatValue(
                                            selectedErrorLog.details?.source
                                                ?.line ||
                                                selectedErrorLog.source?.line
                                        )}`}
                                    />
                                    <DetailBlock
                                        title="Request Data"
                                        value={
                                            selectedErrorLog.details?.request
                                                ?.request_data
                                        }
                                    />
                                    <DetailBlock
                                        title="Query Params"
                                        value={
                                            selectedErrorLog.details?.request
                                                ?.query_params
                                        }
                                    />
                                    <DetailBlock
                                        title="Response Data"
                                        value={selectedErrorLog.details?.response}
                                    />
                                    <DetailBlock
                                        title="Traceback"
                                        value={
                                            selectedErrorLog.details?.error
                                                ?.traceback
                                        }
                                    />
                                </>
                            ) : (
                                <div className="text-soft">
                                    No error details selected.
                                </div>
                            )}
                        </div>
                        <div className="modal-footer bg-light">
                            <button
                                type="button"
                                className="btn btn-outline-light"
                                data-bs-dismiss="modal"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default BridgeConsole;
